import { useState, useEffect } from 'react';
import { Layout } from '../layouts/Layout';
import { Sidebar } from '../components/Sidebar';
import { Header } from '../components/Header';
import { api } from '../utils/api';
import { toast } from '../utils/toast';
import { CreateCompanyModal, EditCompanyModal, DeleteCompanyModal } from '../components/modals/CompanyModals';
import { CreateGroupModal, EditGroupModal, DeleteGroupModal } from '../components/modals/GroupModals';
import { DeviceAssignmentModal } from '../components/modals/DeviceAssignmentModal';
import type { Company, Group, Device } from '../types';

export default function GroupManagement() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [showCreateCompany, setShowCreateCompany] = useState(false);
  const [showEditCompany, setShowEditCompany] = useState(false);
  const [showDeleteCompany, setShowDeleteCompany] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showEditGroup, setShowEditGroup] = useState(false);
  const [showDeleteGroup, setShowDeleteGroup] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);

  const [showAssignDevice, setShowAssignDevice] = useState(false);

  // Fetch companies on mount
  useEffect(() => {
    fetchCompanies();
  }, []);

  // Fetch groups when company is selected
  useEffect(() => {
    if (selectedCompanyId) {
      fetchGroups(selectedCompanyId);
    } else {
      setGroups([]);
    }
  }, [selectedCompanyId]);

  // Fetch devices when group is selected
  useEffect(() => {
    if (selectedGroupId) {
      fetchDevices(selectedGroupId);
    } else {
      setDevices([]);
    }
  }, [selectedGroupId]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const data = await api.getAllCompanies();
      setCompanies(data);
      // Default expand first company
      if (data.length > 0) {
        setSelectedCompanyId(data[0].id);
      }
      setError(null);
    } catch (err) {
      setError('Failed to load companies');
      console.error('Error fetching companies:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async (companyId: string) => {
    try {
      const data = await api.getAllGroups(companyId);
      setGroups(data);
    } catch (err) {
      console.error('Error fetching groups:', err);
    }
  };

  const fetchDevices = async (groupId: string) => {
    try {
      const data = await api.getGroupDevices(groupId);
      setDevices(data);
    } catch (err) {
      console.error('Error fetching devices:', err);
    }
  };

  const handleCompanySelect = (companyId: string) => {
    setSelectedCompanyId(companyId);
    setSelectedGroupId(null);
  };

  const handleGroupSelect = (groupId: string) => {
    setSelectedGroupId(groupId);
  };

  // Company CRUD handlers
  const handleCreateCompany = async (data: any) => {
    await api.createCompany(data);
    toast.success('Company created successfully');
    await fetchCompanies();
  };

  const handleEditCompany = async (id: string, data: any) => {
    await api.updateCompany(id, data);
    toast.success('Company updated successfully');
    await fetchCompanies();
  };

  const handleDeleteCompany = async (id: string) => {
    await api.deleteCompany(id);
    toast.success('Company deleted successfully');
    if (selectedCompanyId === id) {
      setSelectedCompanyId(null);
      setSelectedGroupId(null);
    }
    await fetchCompanies();
  };

  // Group CRUD handlers
  const handleCreateGroup = async (data: any) => {
    await api.createGroup(data);
    toast.success('Group created successfully');
    if (selectedCompanyId) {
      await fetchGroups(selectedCompanyId);
    }
  };

  const handleEditGroup = async (id: string, data: any) => {
    await api.updateGroup(id, data);
    toast.success('Group updated successfully');
    if (selectedCompanyId) {
      await fetchGroups(selectedCompanyId);
    }
  };

  const handleDeleteGroup = async (id: string) => {
    await api.deleteGroup(id);
    toast.success('Group deleted successfully');
    if (selectedGroupId === id) {
      setSelectedGroupId(null);
    }
    if (selectedCompanyId) {
      await fetchGroups(selectedCompanyId);
    }
  };

  // Device assignment handlers
  const handleDeviceAssigned = async () => {
    if (selectedGroupId) {
      await fetchDevices(selectedGroupId);
    }
  };

  const handleRemoveDevice = async (deviceId: string) => {
    if (!confirm('Are you sure you want to remove this device from the group?')) {
      return;
    }
    try {
      await api.removeDeviceFromGroup(deviceId);
      toast.success('Device removed from group');
      if (selectedGroupId) {
        await fetchDevices(selectedGroupId);
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to remove device');
    }
  };

  return (
    <Layout>
      <Sidebar />
      <Header title="Group / Divisi Management" />

      {/* Modals */}
      <CreateCompanyModal
        isOpen={showCreateCompany}
        onClose={() => setShowCreateCompany(false)}
        onSubmit={handleCreateCompany}
      />
      <EditCompanyModal
        isOpen={showEditCompany}
        company={selectedCompany}
        onClose={() => setShowEditCompany(false)}
        onSubmit={handleEditCompany}
      />
      <DeleteCompanyModal
        isOpen={showDeleteCompany}
        company={selectedCompany}
        onClose={() => setShowDeleteCompany(false)}
        onConfirm={handleDeleteCompany}
      />
      <CreateGroupModal
        isOpen={showCreateGroup}
        companies={companies}
        selectedCompanyId={selectedCompanyId}
        onClose={() => setShowCreateGroup(false)}
        onSubmit={handleCreateGroup}
      />
      <EditGroupModal
        isOpen={showEditGroup}
        group={selectedGroup}
        onClose={() => setShowEditGroup(false)}
        onSubmit={handleEditGroup}
      />
      <DeleteGroupModal
        isOpen={showDeleteGroup}
        group={selectedGroup}
        onClose={() => setShowDeleteGroup(false)}
        onConfirm={handleDeleteGroup}
      />
      <DeviceAssignmentModal
        isOpen={showAssignDevice}
        groupId={selectedGroupId || ''}
        onClose={() => setShowAssignDevice(false)}
        onEmployeeAssigned={handleDeviceAssigned}
      />

      <main className="ml-0 md:ml-64 pt-20 p-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Sidebar - Company/Group Tree View */}
          <div className="lg:col-span-4">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-800">Companies & Groups</h2>
                {/* <button
                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                  onClick={() => setShowCreateCompany(true)}
                >
                  + Company
                </button> */}
              </div>

              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading...</p>
                </div>
              ) : error ? (
                <div className="text-center py-8 text-red-600">
                  {error}
                </div>
              ) : companies.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No companies found. Create one to get started.
                </div>
              ) : (
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {companies.map((company) => (
                    <div key={company.id} className="rounded-lg">
                      {/* Company Item */}
                      <div
                        className={`p-3 cursor-pointer hover:bg-gray-50 flex justify-between items-center ${
                          selectedCompanyId === company.id ? 'bg-blue-50' : ''
                        }`}
                        onClick={() => handleCompanySelect(company.id)}
                      >
                        <div className="flex items-center">
                          <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                          <span className="font-medium text-gray-800">{company.nama}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <button
                            className="p-1 text-gray-500 hover:text-blue-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCompany(company);
                              setShowEditCompany(true);
                            }}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            className="p-1 text-gray-500 hover:text-red-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCompany(company);
                              setShowDeleteCompany(true);
                            }}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      {/* Groups under this company */}
                      {selectedCompanyId === company.id && (
                        <div className="border-t bg-gray-50">
                          <div className="p-2">
                            <button
                              className="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100 text-gray-700"
                              onClick={() => setShowCreateGroup(true)}
                            >
                              + Add Group
                            </button>
                          </div>
                          {groups.length === 0 ? (
                            <div className="px-4 py-3 text-sm text-gray-500 text-center">
                              No groups yet
                            </div>
                          ) : (
                            <div className="space-y-1 pb-2">
                              {groups.map((group) => (
                                <div
                                  key={group.id}
                                  className={`mx-2 px-3 py-2 cursor-pointer hover:bg-white rounded flex justify-between items-center ${
                                    selectedGroupId === group.id ? 'bg-white border border-blue-300' : ''
                                  }`}
                                  onClick={() => handleGroupSelect(group.id)}
                                >
                                  <div className="flex items-center">
                                    <svg className="w-4 h-4 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                    <span className="text-sm text-gray-700">{group.name}</span>
                                  </div>
                                  <div className="flex items-center space-x-1">
                                    <button
                                      className="p-1 text-gray-400 hover:text-blue-600"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedGroup(group);
                                        setShowEditGroup(true);
                                      }}
                                    >
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                      </svg>
                                    </button>
                                    <button
                                      className="p-1 text-gray-400 hover:text-red-600"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedGroup(group);
                                        setShowDeleteGroup(true);
                                      }}
                                    >
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Main Content - Device List and Assignment */}
          <div className="lg:col-span-8">
            <div className="bg-white rounded-lg shadow p-4">
              {!selectedGroupId ? (
                <div className="text-center py-16">
                  <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-600 mb-2">Select a Group</h3>
                  <p className="text-gray-500">Choose a group from the sidebar to view and manage its devices</p>
                </div>
              ) : (
                <>
                  <div className="flex justify-between items-center mb-6">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-800">Group Devices</h2>
                      <p className="text-sm text-gray-500 mt-1">
                        {groups.find(g => g.id === selectedGroupId)?.name}
                      </p>
                    </div>
                    <button
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                      onClick={() => setShowAssignDevice(true)}
                    >
                      + Assign Device
                    </button>
                  </div>

                  {devices.length === 0 ? (
                    <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
                      <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                      </svg>
                      <p className="text-gray-500">No devices assigned to this group yet</p>
                      <button
                        className="mt-3 text-blue-600 hover:text-blue-700 text-sm font-medium"
                        onClick={() => setShowAssignDevice(true)}
                      >
                        Assign your first device
                      </button>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Employee Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Device Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              MAC Address
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {devices.map((device) => (
                            <tr key={device.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {device.employee?.Nama || 'N/A'}
                                </div>
                                {device.employee?.NIP && (
                                  <div className="text-xs text-gray-500">NIP: {device.employee.NIP}</div>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">{device.name || 'Unnamed Device'}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-500">{device.mac || 'N/A'}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  device.status === 'active' ? 'bg-green-100 text-green-800' :
                                  device.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {device.status}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button
                                  className="text-red-600 hover:text-red-900"
                                  onClick={() => handleRemoveDevice(device.id)}
                                >
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </main>
    </Layout>
  );
}
